/** Project changes stage all owner decisions before any accepted discard runs. */
export interface ContextLeaveGuard {
  dirty: () => boolean;
  request: () => Promise<boolean>;
  commit?: () => void;
  cancel?: () => void;
}
export interface ContextLeavePlan {
  commit: () => Promise<boolean>;
  cancel: () => void;
}
export interface ProjectLeavePlan extends ContextLeavePlan {
  commitKind: "save" | "discard";
}
export interface ProjectContextOwner {
  pending: () => boolean;
  prepare: () => Promise<ProjectLeavePlan | null>;
}
const guards = new Set<ContextLeaveGuard>();
const projectOwners = new Set<ProjectContextOwner>();
let contextPending = false,
  projectPending = false;
export function registerContextLeaveGuard(guard: ContextLeaveGuard) {
  guards.add(guard);
  return () => {
    guards.delete(guard);
  };
}
export function registerProjectContextOwner(owner: ProjectContextOwner) {
  projectOwners.add(owner);
  return () => {
    projectOwners.delete(owner);
  };
}
export function hasContextDraft() {
  return [...guards].some((guard) => guard.dirty());
}
export function hasProjectContextWork() {
  return (
    hasContextDraft() || [...projectOwners].some((owner) => owner.pending())
  );
}
async function prepareContextLeave(
  owner?: ContextLeaveGuard,
): Promise<ContextLeavePlan | null> {
  if (contextPending) return null;
  contextPending = true;
  const current = [...guards].filter(
    (guard) => (!owner || guard === owner) && guard.dirty(),
  );
  if (owner && !guards.has(owner)) {
    contextPending = false;
    return null;
  }
  let finished = false;
  const cancel = () => {
    if (finished) return;
    finished = true;
    contextPending = false;
    current.forEach((guard) => guard.cancel?.());
  };
  try {
    for (const guard of current) {
      if (
        !guards.has(guard) ||
        !(await guard.request()) ||
        !guards.has(guard)
      ) {
        cancel();
        return null;
      }
    }
    return {
      cancel,
      commit: async () => {
        if (finished || current.some((guard) => !guards.has(guard))) {
          cancel();
          return false;
        }
        current.forEach((guard) => guard.commit?.());
        finished = true;
        contextPending = false;
        return true;
      },
    };
  } catch {
    cancel();
    return null;
  }
}
export async function requestContextLeave(
  owner?: ContextLeaveGuard,
): Promise<boolean> {
  const plan = await prepareContextLeave(owner);
  return plan ? plan.commit() : false;
}
export async function requestProjectContextLeave(): Promise<boolean> {
  if (projectPending) return false;
  projectPending = true;
  const plans: ProjectLeavePlan[] = [];
  let engineering: ContextLeavePlan | null = null;
  try {
    for (const owner of [...projectOwners].filter((owner) => owner.pending())) {
      const plan = await owner.prepare();
      if (plan) plans.push(plan);
      if (!plan || !projectOwners.has(owner)) return false;
    }
    engineering = await prepareContextLeave();
    if (!engineering) return false;
    // Every fallible save/keep must finish before any composer/quote discard.
    for (const kind of ["save", "discard"] as const)
      for (const plan of plans.filter((plan) => plan.commitKind === kind))
        if (!(await plan.commit())) return false;
    return await engineering.commit();
  } catch {
    return false;
  } finally {
    plans.forEach((plan) => plan.cancel());
    engineering?.cancel();
    projectPending = false;
  }
}
